/**
 * YearEndHeroSection
 *
 * 연말정산 히어로 섹션 - Editorial + Big Typography 스타일
 * IMD Annual Report 레퍼런스 기반
 * - MagazineSectionContainer 타이틀 스타일
 * - 프로필 아바타 + 유저명
 * - 2열 그리드 + LineGrid 스타일 보더
 * - Framer Motion 기반 스태거드 카운트업 애니메이션
 */

import React from 'react';
import { Box, Stack, Typography, Divider, Skeleton, Avatar, Grid } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { Section } from '../../components/Container/Section';
import { BigStatRow } from '../../components/YearEnd';
import FloatingWordsBackground from '../../components/YearEnd/FloatingWordsBackground';
import { useIsMobile } from '../../hooks/useIsMobile';
import YearEndSectionTitle from './YearEndSectionTitle';
import LineGrid from '../magazine/LineGrid';
import TypingEffect from '../../components/patterns/typoraphy/TypingEffect';

// 연말정산 영상 URL
const YEAR_REVIEW_VIDEO = 'https://firebasestorage.googleapis.com/v0/b/my-threads-insight.firebasestorage.app/o/yearEnd%2F2025%2Fyear_review.mp4?alt=media';

// 애니메이션 설정
const ANIMATION_CONFIG = {
  duration: 2.5,        // 카운트업 지속 시간 (초)
  delayInterval: 200,   // 행별 딜레이 간격 (ms)
};

// 통계 항목 정의
const getStatItems = (stats, t) => [
  {
    key: 'threads',
    value: stats?.totalThreads ?? 0,
    label: t('yearEnd.hero.stats.threads', { defaultValue: '총 스레드' }),
    type: 'threads',
  },
  {
    key: 'views',
    value: stats?.totalViews ?? 0,
    label: t('yearEnd.hero.stats.views', { defaultValue: '누적 조회수' }),
    type: 'view',
    prefix: '+',
  },
  {
    key: 'likes',
    value: stats?.totalLikes ?? 0,
    label: t('yearEnd.hero.stats.likes', { defaultValue: '누적 좋아요' }),
    type: 'like',
  },
  {
    key: 'replies',
    value: stats?.totalReplies ?? 0,
    label: t('yearEnd.hero.stats.replies', { defaultValue: '누적 답글' }),
    type: 'reply',
  },
  {
    key: 'reposts',
    value: stats?.totalReposts ?? 0,
    label: t('yearEnd.hero.stats.reposts', { defaultValue: '누적 리포스트' }),
    type: 'repost',
  },
  ...(stats?.mostActiveMonth ? [{
    key: 'month',
    value: parseInt(stats.mostActiveMonth, 10),
    label: t('yearEnd.hero.stats.mostActiveMonth', { defaultValue: '가장 활발했던 월' }),
    type: 'month',
  }] : []),
];

export const YearEndHeroSection = ({ year, profile, stats, threadTitles = [], isLoading = false }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useIsMobile('xl');

  // 통계 항목 배열
  const statItems = getStatItems(stats, t);

  // LineGrid gap 설정
  const gridGap = isMobile ? 32 : 64;

  return (
    <Section>
      {/* 매거진 스타일 타이틀 */}
      <YearEndSectionTitle
        title={t('yearEnd.hero.title', { year, defaultValue: `${year} 연말정산` })}
      >
        <Box
          sx={{
            width: '100%',
          }}
        >
          {/* 프로필 + 영상 영역 */}
          <Grid
            container
            spacing={isMobile ? 2 : 3}
            sx={{
              py: isMobile ? 2 : 4,
              mb: isMobile ? 3 : 4,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* 배경: 떠다니는 텍스트 (z-index: 1) */}
            {threadTitles.length > 0 && (
              <FloatingWordsBackground
                texts={threadTitles}
                count={10}
                sx={{ zIndex: 1 }}
              />
            )}

            {/* 좌측: 프로필 영역 (z-index: 2) */}
            <Grid size={{ xs: 12, xl: 8 }} sx={{ position: 'relative', zIndex: 2 }}>
              <Stack
                direction="row"
                spacing={isMobile ? 2 : 3}
                alignItems="center"
                sx={{ height: '100%' }}
              >
                {/* 아바타 */}
                <Box
                  sx={{
                    width: 'fit-content',
                    height: 'fit-content',
                    borderRadius: '50%',
                    padding: 0.5,
                    border: `3px solid ${theme.palette.primary.main}`,
                  }}
                >
                  {isLoading ? (
                    <Skeleton
                      variant="circular"
                      width={isMobile ? 56 : 72}
                      height={isMobile ? 56 : 72}
                    />
                  ) : (
                    <Avatar
                      sx={{
                        width: isMobile ? 56 : 72,
                        height: isMobile ? 56 : 72,
                      }}
                      src={profile?.threads_profile_picture_url}
                    />
                  )}
                </Box>

                {/* 유저 정보 */}
                <Stack spacing={isMobile ? 0.5 : 1}>
                  {isLoading ? (
                    <>
                      <Skeleton variant="text" width={isMobile ? 200 : 350} height={isMobile ? 32 : 48} />
                      <Skeleton variant="text" width={isMobile ? 120 : 180} height={isMobile ? 18 : 20} />
                    </>
                  ) : (
                    <>
                      {/* 타이핑 효과 타이틀 */}
                      <TypingEffect
                        texts={[t('yearEnd.hero.greeting', {
                          username: profile?.username || '',
                          defaultValue: `${profile?.username || ''}님, 1년 동안 수고하셨습니다.`
                        })]}
                        variant={{ xs: 'h5', sm: 'h4', md: 'h4', lg: 'h3' }}
                        textColor={theme.palette.primary.main}
                        fontFamily={'"RIDIBatang", "Noto Serif KR", serif'}
                        fontWeight={700}
                        textAlign="left"
                        cursorType="circle"
                        cursorColor={theme.palette.primary.main}
                        typingSpeed={60}
                        deleteSpeed={30}
                        respectInView={false}
                        persistKey={`yearend-hero:${profile?.username || ''}`}
                        loop={false}
                      />
                      <Typography
                        variant={isMobile ? 'body2' : 'body1'}
                        sx={{
                          color: theme.palette.text.secondary,
                          fontWeight: 400,
                        }}
                      >
                        {t('yearEnd.hero.subtitle', { year, defaultValue: `${year} 활동 리뷰` })}
                      </Typography>
                    </>
                  )}
                </Stack>
              </Stack>
            </Grid>

            {/* 우측: 영상 영역 (z-index: 0 - 가장 밑) */}
            <Grid size={{ xs: 12, xl: 4 }} sx={{ position: 'relative', zIndex: 0 }}>
              <Box
                sx={{
                  width: '100%',
                  aspectRatio: '1/1',
                  overflow: 'hidden',
                  bgcolor: 'grey.100',
                }}
              >
                <video
                  src={YEAR_REVIEW_VIDEO}
                  autoPlay
                  loop
                  muted
                  playsInline
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              </Box>
            </Grid>
          </Grid>

          {/* 구분선 */}
          <Divider sx={{ borderColor: theme.palette.divider, mb: isMobile ? 2 : 3 }} />

          {/* 2열 통계 그리드 - LineGrid 컴포넌트 재사용 */}
          <LineGrid gap={gridGap} breakpoint="xl">
            {statItems.map((item, index) => (
              <Grid
                key={item.key}
                size={{ xs: 6, xl: 6 }}
              >
                <BigStatRow
                  value={item.value}
                  label={item.label}
                  type={item.type}
                  prefix={item.prefix}
                  enableAnimation={!isLoading}
                  isMobile={isMobile}
                  isLoading={isLoading}
                  duration={ANIMATION_CONFIG.duration}
                  delay={ANIMATION_CONFIG.delayInterval * index}
                />
              </Grid>
            ))}
          </LineGrid>
        </Box>
      </YearEndSectionTitle>
    </Section>
  );
};

export default YearEndHeroSection;
